<template>
  <div class="pa-2 tabla-cashflow">
    <div v-if="loading" class="pa-6 text-center grey--text">Cargando...</div>

    <v-simple-table v-else-if="data" dense fixed-header>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left col-detalle">Cliente/Proveedor</th>
            <th class="text-left">Nro.</th>
            <th class="text-left">F/Ingreso</th>
            <th class="text-left">F/Cobro</th>
            <th class="text-right">A días</th>
            <th v-for="col in data.columnas" :key="'h-' + col.id" class="text-right">{{ col.name }}</th>
            <th class="text-right font-weight-bold">TOTAL</th>
          </tr>
        </thead>

        <tbody>
          <!-- Saldo de inicio de cada dia: el saldo al cierre del anterior. -->
          <tr class="yellow lighten-4">
            <td class="font-weight-bold col-detalle yellow lighten-4">SALDO DE INICIO</td>
            <td colspan="4"></td>
            <td v-for="col in data.columnas" :key="'ini-' + col.id" class="text-right font-weight-bold" :class="signClass(data.totales.saldo_inicio[col.id])">
              {{ formatCell(data.totales.saldo_inicio[col.id]) }}
            </td>
            <td></td>
          </tr>

          <template v-for="bloque in data.bloques">
            <tr :key="'b-' + bloque.id" :class="blockClass(bloque.tipo)">
              <td class="font-weight-bold text-uppercase col-detalle" :class="blockClass(bloque.tipo)">{{ bloque.name }}</td>
              <td :colspan="4 + data.columnas.length + 1"></td>
            </tr>

            <tr v-for="fila in bloque.filas" :key="'b-' + bloque.id + '-c-' + fila.cheque_id">
              <td class="col-detalle">
                {{ fila.entidad }}
                <div v-if="fila.bank_name" class="banco">{{ fila.bank_name }}</div>
              </td>
              <td>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <a class="nro-cheque" v-bind="attrs" v-on="on">{{ fila.number }}</a>
                  </template>
                  <v-list dense>
                    <v-list-item dense @click="$emit('accion', { fila: fila, action: 'ver' })">
                      <v-list-item-title>Ver detalle</v-list-item-title>
                    </v-list-item>
                    <template v-if="update_access">
                      <v-divider v-if="accionesDe(fila).length > 0"></v-divider>
                      <v-list-item v-for="accion of accionesDe(fila)" :key="accion.id" dense @click="$emit('accion', { fila: fila, action: accion.id })">
                        <v-list-item-title>{{ accion.name }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="accionesDe(fila).length === 0" dense disabled>
                        <v-list-item-title class="grey--text">Sin acciones disponibles</v-list-item-title>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-menu>
              </td>
              <td>{{ formatDate(fila.ingreso_date) }}</td>
              <td>{{ formatDate(fila.payment_date) }}</td>
              <td class="text-right">{{ fila.dias }}</td>
              <td
                v-for="col in data.columnas"
                :key="'b-' + bloque.id + '-c-' + fila.cheque_id + '-' + col.id"
                class="text-right"
                :class="signClass(fila.valores[col.id])"
              >
                {{ formatCell(fila.valores[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="signClass(fila.total)">{{ formatCell(fila.total) }}</td>
            </tr>

            <tr v-if="bloque.filas.length === 0" :key="'b-' + bloque.id + '-vacio'">
              <td class="col-detalle grey--text">Sin cheques</td>
              <td :colspan="4 + data.columnas.length + 1"></td>
            </tr>

            <tr :key="'b-' + bloque.id + '-tot'" class="grey lighten-3">
              <td class="font-weight-bold col-detalle grey lighten-3">Total {{ bloque.name }}</td>
              <td colspan="4"></td>
              <td v-for="col in data.columnas" :key="'b-' + bloque.id + '-tot-' + col.id" class="text-right font-weight-bold" :class="signClass(bloque.totales[col.id])">
                {{ formatCell(bloque.totales[col.id]) }}
              </td>
              <td class="text-right font-weight-bold" :class="signClass(bloque.total)">{{ formatCell(bloque.total) }}</td>
            </tr>
          </template>

          <tr class="blue lighten-4">
            <td class="font-weight-bold col-detalle blue lighten-4">RESULTADO DEL DÍA</td>
            <td colspan="4"></td>
            <td v-for="col in data.columnas" :key="'res-' + col.id" class="text-right font-weight-bold" :class="signClass(data.totales.resultado_dia[col.id])">
              {{ formatCell(data.totales.resultado_dia[col.id]) }}
            </td>
            <td class="text-right font-weight-bold" :class="signClass(data.totales.total)">{{ formatCell(data.totales.total) }}</td>
          </tr>

          <tr class="yellow lighten-4">
            <td class="font-weight-bold col-detalle yellow lighten-4">SALDO AL CIERRE</td>
            <td colspan="4"></td>
            <td v-for="col in data.columnas" :key="'cie-' + col.id" class="text-right font-weight-bold" :class="signClass(data.totales.saldo_cierre[col.id])">
              {{ formatCell(data.totales.saldo_cierre[col.id]) }}
            </td>
            <td></td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div v-else class="pa-6 text-center grey--text">{{ empty_text }}</div>
  </div>
</template>

<script>
import { formatUtils } from "@/libs/utils/formatUtils";
import ChequesAcciones from "@/constants/chequesAcciones";

export default {
  props: {
    data: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    empty_text: {
      type: String,
      default: "Seleccioná un emisor y un período, y presioná Buscar.",
    },
    update_access: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      block_classes: {
        ingreso: "green lighten-4",
        egreso: "red lighten-4",
      },
    };
  },
  methods: {
    // La matriz es mayormente ceros (dias sin vencimientos). Se marcan con un guion para que se
    // vea de un vistazo donde hay movimiento real.
    formatCell(value) {
      if (!this.hasValue(value)) return "-";
      return formatUtils.formatPrice(value);
    },
    formatDate(value) {
      if (!value) return "-";
      const [y, m, d] = value.split("-");
      return d + "/" + m + "/" + y;
    },
    hasValue(value) {
      return Math.abs(parseFloat(value) || 0) > 0;
    },
    blockClass(tipo) {
      return this.block_classes[tipo] || "grey lighten-3";
    },
    accionesDe(fila) {
      return ChequesAcciones.accionesDe(fila.state, fila.is_own);
    },
    // Todo el reporte es disponibilidad futura: un negativo es plata que sale, y hay que verlo.
    signClass(value) {
      return parseFloat(value) < 0 ? "red--text" : "";
    },
  },
};
</script>

<style scoped>
/* El encabezado lo fija la prop fixed-header de la tabla, que ya trae el fondo y el box-shadow que
   reemplaza al borde inferior. Lo unico que le falta es un tope de altura: sin el, el wrapper crece
   con el contenido, el scroll vertical termina siendo el de la pagina y el encabezado se va con
   ella. El descuento deja lugar para la barra fija, los filtros y los avisos de arriba. */
.tabla-cashflow >>> .v-data-table__wrapper {
  max-height: calc(100vh - 300px);
  min-height: 300px;
}
.tabla-cashflow >>> th,
.tabla-cashflow >>> td {
  white-space: nowrap;
}
/* El scroll horizontal deja siempre a la vista de que cheque es cada fila. */
.tabla-cashflow >>> .col-detalle {
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: white;
}
/* El banco acompana al titular: mismo dato, segundo en importancia. */
.tabla-cashflow >>> .banco {
  font-size: 0.72rem;
  line-height: 1.1;
  color: rgba(0, 0, 0, 0.6);
}
/* Las filas de la matriz son altas de por si; con dos lineas conviene aflojar el alto fijo. */
.tabla-cashflow >>> tbody td.col-detalle {
  height: auto;
  padding-top: 4px;
  padding-bottom: 4px;
}
.tabla-cashflow >>> .nro-cheque {
  color: #1976d2;
  cursor: pointer;
}
.tabla-cashflow >>> .nro-cheque:hover {
  text-decoration: underline;
}
/* La esquina queda pegada por arriba y por izquierda: tiene que pintar sobre el resto del
   encabezado, que Vuetify deja en z-index 2. */
.tabla-cashflow >>> thead th.col-detalle {
  z-index: 3;
}
</style>
